import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@crayon/design-system-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { ConnectionState, Target } from "api/client.generated";
import { ValidationException } from "api/client.generated.extensions";
import FormCheckbox from "components/primitives/FormCheckbox";
import FormErrorMessage from "components/primitives/FormErrorMessage";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import useSourcePartnerConnections from "hooks/api/PartnerMigration/useSourcePartnerConnections";
import useApi from "hooks/api/useApi";
import { ChecklistIcon } from "images/MuiIcons";
import { useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import NotificationMessage from "types/notification-message";
import * as yup from "yup";
import { useAuthorizedTargetsContext } from "../AuthorizedTargetsContext";
import MigratePartnerStepProps from "../MigratePartnerStepProps";
import MigrationFormActionButtons from "../MigrationFormActionButtons";
import WarningIconWithTooltip, { TooltipMessage } from "../WarningIconWithTooltip";

const TargetConnectionsStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const { authorizedTargets } = useAuthorizedTargetsContext();
  const { sourcePartnerConnections, isSourcePartnerConnectionsFetching } =
    useSourcePartnerConnections();
  const { partnersMigrationClient } = useApi();
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const formSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        target: yup.string().oneOf(Object.values(Target)).required(),
        isTargetAuthorized: yup.boolean(),
        checked: yup.boolean(),
      }),
    ),
  });

  type SchemaType = yup.InferType<typeof formSchema>;

  const { control, handleSubmit } = useForm<SchemaType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items", keyName: "fieldId" });
  const fieldsWatch = useWatch({ control, name: "items" });

  useEffect(() => {
    if (isSourcePartnerConnectionsFetching || !sourcePartnerConnections) return;

    // if rerender, clean up fields to avoid duplication
    if (fields.length > 0) remove([...Array(fields.length).keys()]);

    Object.keys(sourcePartnerConnections?.configuredTargets ?? {})
      .filter(
        (x: Target) => sourcePartnerConnections?.configuredTargets[x] === ConnectionState.Connected,
      )
      .forEach((x: Target) => {
        append({
          target: x,
          checked: false,
          isTargetAuthorized: authorizedTargets.some((t) => t === x),
        });
      });
  }, [
    authorizedTargets,
    sourcePartnerConnections,
    isSourcePartnerConnectionsFetching,
    append,
    remove,
    fields.length,
  ]);

  const onSave = async (formData: SchemaType): Promise<void> => {
    setIsSaving(true);

    try {
      const targetsToMigrate: Target[] =
        formData.items?.filter((x) => x.checked).map((x) => x.target) ?? [];

      if (targetsToMigrate.length === 0) return;

      await partnersMigrationClient.migrateTargetConnections(targetsToMigrate);

      onStepComplete();
    } catch (e: unknown) {
      const validationError = ValidationException.parse(e);
      if (validationError) {
        setFormErrorMsg(validationError.getFirstErrorMessage());
      } else {
        setFormErrorMsg(NotificationMessage.UNKNOWN_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const DataTableHead = (
    <TableHead>
      <TableRow>
        <TableCell size="small" sx={{ width: "45px" }}>
          <Box display="flex">
            <ChecklistIcon />
          </Box>
        </TableCell>
        <TableHeaderCell columnName="Target" />
      </TableRow>
    </TableHead>
  );

  const DataTableBody = (
    <TableBody>
      {fields.map((x, i) => (
        <TableRow key={x.fieldId}>
          <TableCell>
            <FormCheckbox
              control={control}
              bindSchemaFieldName={`items.${i}.checked`}
              label=""
              disabled={!x.isTargetAuthorized || isSaving}
            />
          </TableCell>
          <TableBodyCell
            label={x.target}
            {...(!x.isTargetAuthorized && {
              icon: <WarningIconWithTooltip message={TooltipMessage.PsaNotAuthorized} />,
            })}
          />
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Box display="flex" flexDirection="column" gap={1} minWidth="500px" width="40%">
        <TableContainer component={Paper}>
          <Table testId="migrate-targets-table">
            {DataTableHead}
            {isSourcePartnerConnectionsFetching ? (
              <TableBodySkeleton colsCount={2} rowsCount={3} />
            ) : (
              DataTableBody
            )}
          </Table>
        </TableContainer>
        <FormErrorMessage message={formErrorMsg} />
        <MigrationFormActionButtons
          isSaveDisable={!fieldsWatch?.some((x) => x.checked)}
          isSaveLoading={isSaving}
        />
      </Box>
    </form>
  );
};

export default TargetConnectionsStep;
